import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { DashboardLayoutTemplateViewStore } from "../../store";
import { DashboardLayoutTemplateView } from "../view/DashboardLayoutTemplateView";
import { DashboardLayoutTemplateUpdateStore } from "../../store";
import { DashboardLayoutTemplateUpdate } from "../update/DashboardLayoutTemplateUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type DashboardLayoutTemplateModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const DashboardLayoutTemplateModalContent: React.FC<DashboardLayoutTemplateModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new DashboardLayoutTemplateUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new DashboardLayoutTemplateViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <DashboardLayoutTemplateView
            dashboardLayoutTemplateViewStore={
              storeInfo.store as DashboardLayoutTemplateViewStore
            }
          />
        )}
        {storeInfo.type === "update" && (
          <DashboardLayoutTemplateUpdate
            dashboardLayoutTemplateUpdateStore={
              storeInfo.store as DashboardLayoutTemplateUpdateStore
            }
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
